import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import SubmitButton from "../components/SubmitButton";
import SearchBar from "../components/SearchBar";
import TabFilter from "../components/TabFilter";
import StoreComparison from "../components/StoreComparison";
import AppBar from "../components/AppBar";
import { useAuthStore } from "../stores/authStore";
import { HOME_NAV_ITEM } from "../types/navItem";

/**
 * 앱 첫 진입(가입 전) 3스텝 온보딩: /onboarding-intro
 * - Splash에서 비로그인 + 미열람이면 여기로 보냄. 다 보거나 건너뛰면 Welcome(/login)으로.
 * - 최초 1회만: 완료/건너뛰기 시 로컬 플래그를 남기고, Splash가 이후엔 건너뜀.
 * - 우상단 진행 점, STEP1 로고 탭 손가락, STEP2 고민 손가락 애니메이션 포함.
 */

// 온보딩 최초 1회 노출용 플래그.
// 키에 버전(_v2)을 붙여, 온보딩을 개편할 때 키만 올리면 기존에 본 유저도 새 온보딩을 다시 1회 본다.
const INTRO_SEEN_KEY = "intro_onboarding_seen_v3";
export const isIntroSeen = (): boolean => {
  try {
    return localStorage.getItem(INTRO_SEEN_KEY) === "1";
  } catch {
    return true; // 저장소 접근 불가 시엔 온보딩을 반복 노출하지 않음(안전)
  }
};
const markIntroSeen = (): void => {
  try {
    localStorage.setItem(INTRO_SEEN_KEY, "1");
  } catch {
    /* noop */
  }
};

const BROWN = "#6E5B4A";

const ProgressDots = ({ step }: { step: number }) => (
  <div className="flex items-center gap-6px">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="h-6px rounded-full transition-all duration-300"
        style={{
          width: i === step ? 22 : 6,
          backgroundColor: i === step ? BROWN : "rgba(75,54,33,0.18)",
        }}
      />
    ))}
  </div>
);

/** 브라운 클릭 손 아이콘 (첨부 이미지, 클릭 반짝이 포함) */
const HandIcon = () => (
  <img
    src="/images/onboarding/tap-hand.png?v=3"
    alt=""
    width={30}
    height={38}
    draggable={false}
    style={{
      display: "block",
      // 컨테이너 폭/Tailwind(max-width:100%) 영향 제거 → STEP1·STEP2 손 크기 동일 보장
      width: 30,
      height: 38,
      maxWidth: "none",
      filter: "drop-shadow(0 3px 4px rgba(0,0,0,0.15))",
    }}
  />
);

/** 터치 손가락 힌트 — 리플은 tap-finger 안(손끝 좌표)에 두어 손과 함께 움직임 */
const TapHint = ({ variant }: { variant: "logo" | "deliberate" }) => (
  <div className={`tap-hint tap-hint--${variant}`} aria-hidden="true">
    <div className="tap-mover">
      <span className="tap-finger">
        <HandIcon />
        <span className="tap-ripple" />
      </span>
    </div>
  </div>
);

// STEP1·STEP2 카드 높이 통일용 최소 높이
const CARD_MIN_H = 232;

const Card: React.FC<{ children: React.ReactNode; minH?: number }> = ({
  children,
  minH,
}) => (
  <div
    className="relative rounded-28px p-24px"
    style={{
      backgroundColor: "#FAF8F3",
      boxShadow: "0 10px 30px rgba(75,54,33,0.07)",
      border: "1px solid rgba(75,54,33,0.05)",
      minHeight: minH,
    }}
  >
    {children}
  </div>
);

const TextBlock = ({
  step,
  title,
  sub,
}: {
  step: number;
  title: React.ReactNode;
  sub: React.ReactNode;
}) => (
  <div>
    <div
      className="text-14px font-bold tracking-wider"
      style={{ color: "#B7AEA1" }}
    >
      STEP {step + 1}
    </div>
    <h1 className="mt-8px text-28px font-800 leading-135% text-4B3621-80%">
      {title}
    </h1>
    <p className="mt-14px text-16px leading-150%" style={{ color: "#6B5E46" }}>
      {sub}
    </p>
  </div>
);

// ── STEP별 카드 내용 ──
// STEP1: 홈 상단바를 실제 컴포넌트 그대로(TASTEE 로고 + bell-icon.png / SearchBar / TabFilter).
// 손이 로고를 탭. 데모라 pointer-events 없음(내부 컴포넌트 클릭 방지).
const CardStep1 = () => (
  <Card>
    <div className="flex flex-col pointer-events-none">
      {/* 상단바: 홈과 동일 (로고 + 종). pb-14로 검색바와 간격만(위쪽은 카드 패딩에 맡겨 위/아래 균형) */}
      <div className="flex items-center justify-between pb-14px">
        <div className="relative shrink-0 flex items-center h-28px">
          <span
            className="font-pretendard font-bold text-22px leading-none text-brandPrimary"
            style={{
              letterSpacing: "0.2em",
              paddingLeft: "0.2em",
              transform: "translateY(1px)",
            }}
          >
            TASTEE
          </span>
          <TapHint variant="logo" />
        </div>
        <img
          src="/images/bell-icon.png"
          alt="알림"
          className="object-contain"
          style={{ width: 28, height: 28 }}
        />
      </div>

      {/* 검색바 (홈과 동일 컴포넌트, mt-8) */}
      <div className="mt-8px">
        <SearchBar placeholder="검색" height={44} />
      </div>

      {/* 추천·팔로잉 탭 (홈과 동일 컴포넌트) — 내부 px-20px를 상쇄해 검색바와 정렬 */}
      <div style={{ marginLeft: -20, marginRight: -20 }}>
        <TabFilter
          options={[
            { value: "recommended", label: "추천" },
            { value: "following", label: "팔로잉" },
          ]}
          activeValue="recommended"
          onChange={() => {}}
        />
      </div>
    </div>
  </Card>
);

// STEP2: 실제 StoreComparison 컴포넌트 그대로(제목·부제·VS·비교카드). 손은 overlay로 카드 위에.
const CardStep2 = () => (
  <Card minH={CARD_MIN_H}>
    {/* StoreComparison 자체 mt-20px·px-20px를 상쇄해 카드에 맞춤 */}
    <div
      className="pointer-events-none"
      style={{ marginLeft: -20, marginRight: -20, marginTop: -20 }}
    >
      <StoreComparison
        store1={{
          id: "1",
          name: "명동교자 본점",
          address: "서울특별시 중구 명동10길 29",
        }}
        store2={{
          id: "2",
          name: "마가만두",
          address: "부산광역시 동구",
        }}
        onSelect={() => {}}
        overlay={<TapHint variant="deliberate" />}
      />
    </div>
  </Card>
);

// 첨부 이미지 그대로(커버+접시+그림자 통짜). 폭 135 고정 → 높이 auto(비율 1.27 유지 ≈106).
// 그림자는 이미지에 내장돼 있으므로 CSS drop-shadow를 얹지 않는다(얹으면 이중 그림자·겹침).
const COVER_W = 135; // 커버 표시 폭(비율 고정)
const CoverOnPlate = ({ src, alt }: { src: string; alt: string }) => (
  <img
    src={src}
    alt={alt}
    draggable={false}
    className="block"
    style={{
      width: COVER_W,
      height: "auto",
      maxWidth: "none",
    }}
  />
);

// STEP3 행 아이콘 — 앱에서 실제 쓰는 이미지 에셋 그대로
// STEP3 행 아이콘 — 앱 실제 에셋, 셋 다 24px 통일
const PersonIcon = () => (
  <img
    src="/images/profile-placeholder-icon.png"
    alt=""
    className="w-24px h-24px rounded-full object-cover"
  />
);
const CheckIcon = () => (
  <img
    src="/images/checkbox-bold-filled.png"
    alt=""
    className="w-24px h-24px object-contain"
  />
);
const BookmarkIcon = ({ filled }: { filled?: boolean }) => (
  <img
    src={filled ? "/images/bookmark-filled.png" : "/images/bookmark-empty.png"}
    alt=""
    className="w-24px h-24px object-contain"
  />
);

const PlaceRow = ({
  name,
  addr,
  left,
  mid,
  right,
}: {
  name: string;
  addr: string;
  left: React.ReactNode;
  mid: React.ReactNode;
  right: React.ReactNode;
}) => (
  <div className="flex items-center gap-10px">
    <div className="flex-1 min-w-0">
      <div className="text-16px font-bold text-4B3621-80% text-ellipsis-1">
        {name}
      </div>
      <div
        className="mt-2px text-13px text-ellipsis-1"
        style={{ color: "#6B5E46" }}
      >
        {addr}
      </div>
    </div>
    <div className="flex items-center gap-14px flex-shrink-0">
      {left}
      {mid}
      {right}
    </div>
  </div>
);

const CardStep3 = () => (
  <Card>
    {/* 각 그룹 폭 = 커버+접시 전체(간격이 접시에 안 먹힘). 라벨은 커버 정사각(왼쪽) 기준 중앙.
        그룹은 flex-shrink-0로 절대 안 찌부러지게(폭 부족 시 겹치던 버그 방지).
        커버 영역만 좌우 음수 마진으로 카드 패딩(24)을 좁혀 접시가 안 잘리게. */}
    <div
      className="flex items-start justify-center"
      style={{ marginLeft: -14, marginRight: -14, gap: 24 }}
    >
      <div className="flex flex-col items-start flex-shrink-0">
        <CoverOnPlate
          src="/images/onboarding/cover_watermelon.png?v=8"
          alt=""
        />
        <div className="mt-10px w-full text-center">
          <div className="text-16px font-bold text-4B3621-80%">먹킷리스트</div>
          <div className="mt-2px text-13px" style={{ color: "#9E958A" }}>
            비공개
          </div>
        </div>
      </div>
      <div className="flex flex-col items-start flex-shrink-0">
        <CoverOnPlate src="/images/onboarding/cover_coffee.png?v=8" alt="" />
        <div className="mt-10px w-full text-center">
          <div className="text-16px font-bold text-4B3621-80%">
            카페 모음.zip
          </div>
          <div className="mt-2px text-13px" style={{ color: "transparent" }}>
            .
          </div>
        </div>
      </div>
    </div>
    <div
      className="my-18px h-1px w-full"
      style={{ backgroundColor: "rgba(75,54,33,0.10)" }}
    />
    <div className="flex flex-col gap-16px">
      <PlaceRow
        name="카페 휙"
        addr="서울특별시 용산구 신흥로14길 88"
        left={<PersonIcon />}
        mid={<CheckIcon />}
        right={<BookmarkIcon />}
      />
    </div>
  </Card>
);

const STEPS = [
  {
    card: <CardStep1 />,
    title: <>가본 장소들, 한번에 싹.</>,
    sub: (
      <>
        홈에서 로고 클릭! 붙여넣기 한 번이면
        <br />
        저장해둔 장소를 한꺼번에 불러올 수 있어요
      </>
    ),
  },
  {
    card: <CardStep2 />,
    title: <>둘 중 하나만 고르세요</>,
    sub: (
      <>
        별점 대신 “어디가 좋았지?”에 답하다 보면
        <br />
        나만의 맛집 순위가 완성됩니다
      </>
    ),
  },
  {
    card: <CardStep3 />,
    title: <>플레이트리스트</>,
    sub: (
      <>
        “다음에 여기 가자”가 흩어지지 않게.
        <br />
        친구를 초대해서 같이 채워보세요
      </>
    ),
  },
];

const OnboardingIntro: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // 온보딩을 다 봤거나 건너뛰면 '열람' 플래그를 남기고 이동.
  // 로그인 상태면 홈, 비로그인이면 Welcome(로그인/가입). Splash가 플래그로 다음부턴 건너뜀(최초 1회).
  const finishIntro = () => {
    markIntroSeen();
    const { isAuthenticated } = useAuthStore.getState();
    navigate(isAuthenticated ? HOME_NAV_ITEM.path : "/login", {
      replace: true,
    });
  };

  // 첫 장에서는 버튼 자체를 감춘다(돌아갈 데가 없다). 그래서 여기 올 때는
  // 언제나 step > 0 이다.
  const goBack = () => setStep((s) => Math.max(0, s - 1));
  const goNext = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else finishIntro();
  };

  const cur = STEPS[step];

  return (
    // 앱 웹뷰 기준 h-full(100%). (100dvh는 앱에서 화면 비율이 틀어져 되돌림.)
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <style>{css}</style>
      <div className="app-container flex-1 flex flex-col overflow-hidden pb-32px">
        {/* 헤더: 앱 공용 AppBar(휴대폰번호 입력 화면과 동일 뒤로가기 위치).
            진행 점은 AppBar rightElement 슬롯(48px·edge flush)이 좁아 잘려서, 우측 20px 여백으로 오버레이. */}
        <div className="relative">
          <AppBar showBack={step > 0} onBack={goBack} />
          <div
            className="absolute top-0 h-48px flex items-center"
            style={{ right: 20 }}
          >
            <ProgressDots step={step} />
          </div>
        </div>

        {/* 본문: 카드(상단, 살짝 아래로) + 텍스트(하단) */}
        <div className="flex-1 flex flex-col px-20px overflow-hidden">
          {/* 카드 위/아래는 flex로 흡수 → 카드 높이가 달라도 아래 텍스트 위치는 고정 */}
          <div className="flex-1" />
          <div>{cur.card}</div>
          <div className="flex-1" />
          {/* 하단 텍스트만 좌우 40px (바깥 px-20 + 여기 px-20). 카드는 20px 유지. */}
          <div className="px-20px">
            <TextBlock step={step} title={cur.title} sub={cur.sub} />
          </div>
          {/* 텍스트~'다음으로' 사이 고정 여백 (모든 스텝 동일 위치·동일 간격) */}
          <div className="shrink-0" style={{ height: 72 }} />
        </div>

        {/* 하단 버튼 (앱 표준: 부모 pb-32px) */}
        <div className="w-full px-20px">
          <SubmitButton text="다음으로" onSubmit={goNext} />
        </div>
      </div>
    </div>
  );
};

const css = `
.tap-hint { position:absolute; pointer-events:none; z-index:20; }
.tap-mover { position:absolute; }
.tap-finger { position:absolute; line-height:0; }
.tap-finger img { display:block; }
/* 리플은 손가락 끝(이미지의 약 44%,30% 지점)에 중심을 둔다 */
.tap-ripple { position:absolute; left:44%; top:30%; width:24px; height:24px; border-radius:9999px;
  border:2px solid rgba(111,95,78,0.55); }

/* STEP1: 로고 옆에서 탭 반복 (손끝이 TASTEE 가리킴) */
.tap-hint--logo { left:100%; top:50%; }
.tap-hint--logo .tap-mover { left:4px; top:0; }
.tap-hint--logo .tap-finger { top:-6px; left:-28px; animation: tapPress 1.7s ease-in-out infinite; }
/* 손끝(44%,30%)을 기준으로 회전 → 리플과 손끝 정렬 유지 */
.tap-hint--logo .tap-finger img { transform: rotate(-12deg); transform-origin: 44% 30%; }
.tap-hint--logo .tap-ripple { animation: tapRipple 1.7s ease-in-out infinite; }

@keyframes tapPress {
  0%, 100% { transform: translateY(0) scale(1); }
  14%      { transform: translateY(-7px) scale(0.9); }
  30%      { transform: translateY(0) scale(1); }
}
@keyframes tapRipple {
  0%, 6%   { transform: translate(-50%,-50%) scale(0.4); opacity:0; }
  14%      { transform: translate(-50%,-50%) scale(0.5); opacity:0.55; }
  45%      { transform: translate(-50%,-50%) scale(1.7); opacity:0; }
  100%     { opacity:0; }
}

/* STEP2: 두 박스(25% ↔ 75%) 사이 왕복하며 고민 */
.tap-hint--deliberate { left:0; top:50%; width:100%; height:0; }
.tap-hint--deliberate .tap-mover { top:0; left:25%; transform:translateX(-50%);
  animation: s2moveX 3.8s ease-in-out infinite; }
/* 손끝(이미지 44%지점)이 박스 중앙에 오도록 left 보정 → VS 기준 좌우 대칭 이동 */
.tap-hint--deliberate .tap-finger { top:0px; left:-13px; animation: tapPressY 3.8s ease-in-out infinite; }
/* STEP1과 동일한 손 방향(반시계 12°) */
.tap-hint--deliberate .tap-finger img { transform: rotate(-12deg); transform-origin: 44% 30%; }
.tap-hint--deliberate .tap-ripple { animation: s2ripple2 3.8s ease-in-out infinite; }

@keyframes s2moveX {
  0%,12%   { left:25%; }
  40%,52%  { left:75%; }
  80%,100% { left:25%; }
}
@keyframes tapPressY {
  0%,3%    { transform: translateY(0); }
  6%       { transform: translateY(-7px); }
  10%,42%  { transform: translateY(0); }
  46%      { transform: translateY(-7px); }
  50%,100% { transform: translateY(0); }
}
@keyframes s2ripple2 {
  0%,3%  { transform: translate(-50%,-50%) scale(0.4); opacity:0; }
  6%     { transform: translate(-50%,-50%) scale(0.5); opacity:0.5; }
  22%    { transform: translate(-50%,-50%) scale(1.6); opacity:0; }
  43%    { transform: translate(-50%,-50%) scale(0.4); opacity:0; }
  46%    { transform: translate(-50%,-50%) scale(0.5); opacity:0.5; }
  62%    { transform: translate(-50%,-50%) scale(1.6); opacity:0; }
  100%   { opacity:0; }
}
`;

export default OnboardingIntro;
